import React, { useEffect, useState, useMemo, useRef } from 'react'; // Moved useRef to the top
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { tokens } from '../../../theme';
import { deleteWarehouse, getWarehouses } from '../../../redux/slice/warehouses.slice'; // Update with your actual slice paths
import Header from "../../../components/Header";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { Country, State } from 'country-state-city';
import { getRoles } from '../../../redux/slice/roles.slice';
let country_state_district = require('@coffeebeanslabs/country_state_district');

let countries = country_state_district.getAllCountries();
let districts = country_state_district.getDistrictsByStateId();
let states = country_state_district.getAllStates();

const WarehouseDataGrid = ({ data, columns }) => {
    if (!data || !Array.isArray(data)) {
        return <div>Loading.....</div>
    }
    return (
        <DataGrid
            rows={data}
            columns={columns}
            components={{ Toolbar: GridToolbar }}
            pageSize={5}
            rowsPerPageOptions={[5, 10]}
            density="compact"
            getRowId={(row) => row._id}
            autoHeight
            sx={{
                "@media print": {
                    "& .MuiDataGrid-toolbarContainer": {
                        display: "none !important"
                    },
                    "& .MuiDataGrid-root": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-cell": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-footerContainer": {
                        color: "black !important",
                    },
                    "& .css-7ms3qr-MuiTablePagination-displayedRows": {
                        color: "black !important",
                    },
                },
            }}
        />
    );
};

const Warehouse = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const colors = tokens(theme.palette.mode);
    const warehouses = useSelector(state => state.warehouses.warehouses);

    const [openView, setOpenView] = React.useState(false);
    const [viewData, setViewData] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [wareHouseDelete, setWareHouseDelete] = useState(null);
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery('(max-width:500px)');


    useEffect(() => {
        dispatch(getWarehouses());
    }, [dispatch]); // Keep dispatch in the dependency array

    const handleClickOpen = () => {
        navigate('/admin/add-warehouses');
    };
    const handleClickOpenView = () => {
        setOpenView(true);
    };

    const handleCloseView = () => {
        setOpenView(false);
    };
    const handleView = (data) => {
        handleClickOpenView();
        setViewData(data)
    }

    const handleDelete = (id) => {
        setWareHouseDelete(id);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = () => {
        if (wareHouseDelete) {
            dispatch(deleteWarehouse(wareHouseDelete));
            setDeleteConfirmOpen(false);
            setWareHouseDelete(null);
        }
    }
    const cancelDelete = () => {
        setDeleteConfirmOpen(false);
        setWareHouseDelete(null);
    }
    const handleEdit = (data) => {
        navigate('/admin/add-warehouses', { state: data });
    };

    const columns = useMemo(() => [
        { field: 'wareHouseCode', headerName: 'Warehouse Code', width: 150 },
        { field: 'wareHouseName', headerName: 'Warehouse Name', width: 200 },
        { field: 'contactPersonName', headerName: 'Contact Person Name', width: 250 },
        { field: 'email', headerName: 'Contact Person email', width: 200 },
        { field: 'contactNumber', headerName: 'Contact Number', width: 150 },
        {
            field: 'district', headerName: 'District', width: 150,
            filterable: true,
            valueGetter: (params) => {
                const district = country_state_district.getDistrictsByStateId(params.row.state)?.find((v) => v.id == params.row.district).name
                return district
            },
            renderCell: (params) => {
                const district = country_state_district.getDistrictsByStateId(params.row.state)?.find((v) => v.id == params.row.district).name
                return district
            }
        },
        { field: 'city', headerName: 'City', width: 150 },
        {
            field: 'Action',
            headerName: 'Action',
            width: 130,
            renderCell: (params) => (
                <>
                    <IconButton aria-label="edit" onClick={() => handleView(params.row)}>
                        <RemoveRedEyeIcon />
                    </IconButton>
                    <IconButton aria-label="edit" onClick={() => handleEdit(params.row)}>
                        <EditIcon />
                    </IconButton>
                    <IconButton aria-label="delete" onClick={() => handleDelete(params.row._id)}>
                        <DeleteIcon />
                    </IconButton>
                </>
            )
        }
    ], []);

    return (
        <Box m="2px">
            <Header
                title="WAREHOUSES"
                subtitle="Manage Your Warehouses Here"
            />
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleClickOpen}>
                    Add Warehouse
                </Button>
            </Box>

            <Box
                height="75vh"
                sx={useMemo(() => ({
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },
                    // "& .MuiDataGrid-cell": {
                    //     fontSize: "14px !important",
                    // },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[700],
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiCheckbox-root": {
                        color: `${colors.greenAccent[200]} !important`,
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.grey[100]} !important`,
                        fontSize: "13px !important",
                    },
                    // "& .MuiDataGrid-cellContent": {
                    //     fontSize: "14px !important"
                    // },
                    // "& .css-6hy2kr-MuiDataGrid-root": {
                    //     fontSize: "14px !important"
                    // },
                    // "& .css-1v3msgo-MuiTypography-root-MuiDialogContentText-root": {
                    //     fontSize: "14px !important"
                    // },
                    // "& .css-t89xny-MuiDataGrid-columnHeaderTitle": {
                    //     fontSize: "16px !important"
                    // },
                }), [colors])}
            >
                <WarehouseDataGrid data={warehouses} columns={columns} />
            </Box>
            <Dialog
                fullScreen={fullScreen}
                open={openView}
                onClose={handleCloseView}
                aria-labelledby="responsive-dialog-title"
                maxWidth={"sm"}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title" style={{ backgroundColor: theme.palette.background.purple, textAlign: 'center', fontWeight: 'bold' }}>
                    {"Warehouse Details"}
                </DialogTitle>
                <DialogContent style={{ backgroundColor: theme.palette.background.purple }}>
                    <DialogContentText>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px' }}>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Ware House Code : </strong> {viewData?.wareHouseCode}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Ware House Name : </strong> {viewData?.wareHouseName}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Contact Person Name : </strong> {viewData?.contactPersonName}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Contact Number : </strong> {viewData?.contactNumber}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Email : </strong> {viewData?.email}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Address : </strong> {viewData?.address}</p>

                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Pincode : </strong> {viewData?.pincode}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>District : </strong>  {viewData && viewData.state ?
                                country_state_district.getDistrictsByStateId(viewData.state).find((v) => v.id == viewData.district)?.name
                                : viewData?.district}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>City : </strong> {viewData?.city}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>State : </strong>  {viewData && viewData.country ?
                                country_state_district.getStatesByCountryId(viewData.country)
                                    .find((v) => v.id == viewData.state)?.name
                                : viewData?.state}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Country : </strong>  {viewData ? country_state_district.getAllCountries().find((v) => v.id == viewData.country)?.name : viewData?.country}</p>
                        </div>
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ backgroundColor: theme.palette.background.purple }}>
                    <Button autoFocus onClick={handleCloseView} style={{ color: theme.palette.text.primary }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={deleteConfirmOpen} onClose={cancelDelete}>
                <DialogContent>
                    Are you sure you want to delete this warehouse?
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete} style={{ color: theme.palette.text.primary }}>No</Button>
                    <Button onClick={confirmDelete} style={{ color: theme.palette.text.primary }}>Yes</Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
};

export default Warehouse;
